import { dialog, type DialogMsg, type DialogState } from '@llui/components/dialog';
import {
  div,
  type Mountable,
  type Renderable,
  type Send,
  type Signal,
} from '@llui/dom';

/**
 * `<Modal>` — DungeonLogs sizing and naming over LLui's standard dialog.
 *
 * LLui owns the portal, outside/Escape dismissal, focus trap + restoration,
 * scroll lock, sibling hiding, and ARIA part contract. This adapter retains
 * only the app's conditional-mount API and panel size classes.
 */

export interface ModalLabels {
  /** Accessible label used by LLui's close-trigger part. */
  close: string;
}

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

export interface ModalOptions {
  /** Host-owned open state. Prefer this for reactive app surfaces. */
  open: Signal<boolean>;
  /** Called when LLui dismisses the dialog. */
  onDismiss: () => void;
  /** Optional id of the heading element inside `children`. */
  labelId?: string;
  /** Optional id of the descriptive element inside `children`. */
  describedById?: string;
  /** ARIA role — `dialog` (default) or `alertdialog` for confirms. */
  role?: 'dialog' | 'alertdialog';
  /** Optional CSS class added to the panel for app-specific layout. */
  panelClass?: string;
  /** Optional test id forwarded to the dialog content. */
  testid?: string;
  /** Panel size. Defaults to `md`. */
  size?: ModalSize;
  /** Localised labels. */
  labels: ModalLabels;
  /** Children rendered inside the panel. */
  children: Renderable;
}

let modalSeq = 0;

export function modal(opts: ModalOptions): Mountable {
  const id = `dl-modal-${modalSeq++}`;
  const size = opts.size ?? 'md';
  const panelClass = `dl-modal-panel dl-modal-panel--${size}${opts.panelClass ? ` ${opts.panelClass}` : ''}`;
  const overlay = (state: Signal<DialogState>, send: Send<DialogMsg>): Mountable => {
    const parts = dialog.connect(state, send, {
      id,
      role: opts.role ?? 'dialog',
      closeLabel: opts.labels.close,
    });

    return dialog.overlay({
      state,
      send,
      parts,
      positionerClass: 'dl-modal',
      content: () => [
        div(
          {
            ...parts.content,
            class: panelClass,
            'aria-labelledby': opts.labelId,
            'aria-describedby': opts.describedById,
            'data-testid': opts.testid,
          },
          opts.children,
        ),
      ],
    });
  };

  const controlledState = opts.open.map(
    (open): DialogState => ({
      open,
      status: open ? 'open' : 'closed',
      skipAnimations: true,
    }),
  );
  const controlledSend: Send<DialogMsg> = (msg) => {
    if (
      msg.type === 'close' ||
      msg.type === 'toggle' ||
      (msg.type === 'setOpen' && !msg.open)
    ) {
      opts.onDismiss();
    }
  };

  return div({ class: 'dl-modal-boundary' }, [overlay(controlledState, controlledSend)]);
}
